import { FunctionComponent, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";

import { useLazyGlobalSearchQuery } from "@/pages/Private/redux/search/search.api";
import { hasDisplayableCompany } from "@/components/GlobalSearchModal/utils";
import { getComposedEventTarget } from "@/utils/getComposedEventTarget";
import { Avatar } from "@/components/Avatar/Avatar";
import { Spinner } from "@/components/Spinner/Spinner";

import { useViewportClampedPanel } from "@/utils/useViewportClampedPanel";

import { parseLinkedinProfileUrl } from "../../utils/linkedinProfileUrl";

import type { CompanyEnrichPreviewPerson } from "@/pages/Private/pages/CompanyEnrich/schema/companyEnrich";

const MIN_QUERY_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 400;
const MAX_RESULTS = 5;

/** Splits a display name for the shared Avatar's initials. */
const nameParts = (name: string): { firstName: string; lastName: string } => {
	const parts = name.trim().split(" ");

	return { firstName: parts[0] ?? "", lastName: parts[parts.length - 1] ?? "" };
};

interface TrackAddPersonComboboxProps {
	/** A person picked from the search — carries the enrich id the step submits. */
	onSelectPerson: (person: CompanyEnrichPreviewPerson) => void;
	/** A LinkedIn profile URL the search has no match for, already canonicalised. */
	onSelectUrl: (url: string) => void;
	portalContainer?: HTMLElement | null;
}

/**
 * "Add a person" typeahead of the Track step. The field used to take a raw LinkedIn URL and
 * silently drop anything else, so typing a name looked like a dead control and an accepted URL
 * became a row with no name, company or photo (PREC-3823). It now searches the same CompanyEnrich
 * people index the step's table is built from — so a pick lands as an ordinary preview row — and
 * still recognises a pasted profile URL, exactly like the profile step's own typeahead
 * ({@link ../LinkedinProfileCombobox}).
 */
export const TrackAddPersonCombobox: FunctionComponent<TrackAddPersonComboboxProps> = ({
	onSelectPerson,
	onSelectUrl,
	portalContainer,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`onboarding.track.${key}`);

	const [query, setQuery] = useState("");
	const [isOpen, setIsOpen] = useState(false);
	const [results, setResults] = useState<CompanyEnrichPreviewPerson[]>([]);
	const [hasSearched, setHasSearched] = useState(false);
	const [menuRect, setMenuRect] = useState<{ top: number; left: number; width: number } | null>(
		null
	);

	const anchorRef = useRef<HTMLDivElement>(null);
	const menuRef = useRef<HTMLDivElement>(null);

	// Keeps the portalled panel inside the viewport wherever the trigger sits (PREC-4138).
	useViewportClampedPanel(menuRef, isOpen);

	const [fetchSearch, { isFetching: isSearching }] = useLazyGlobalSearchQuery();

	/** A typed profile URL is used as-is — the same field takes a name or a URL. */
	const typedUrl = parseLinkedinProfileUrl(query);

	useEffect(() => {
		// A URL is not a search query: offer it directly instead of searching for it.
		if (parseLinkedinProfileUrl(query)) {
			setResults([]);
			setHasSearched(false);
			setIsOpen(true);

			return;
		}

		if (query.trim().length < MIN_QUERY_LENGTH) {
			setResults([]);
			setHasSearched(false);
			setIsOpen(false);

			return;
		}

		const handle = setTimeout(async () => {
			try {
				const response = await fetchSearch({
					personSearchSource: { query: query.trim() },
				}).unwrap();

				// Same gate as the global-search tabs and as the step's own suggestions: somebody with
				// no employer behind them cannot be tracked, so they are not offered.
				setResults(
					(response.companyEnrich?.items ?? []).filter(hasDisplayableCompany).slice(0, MAX_RESULTS)
				);
			} catch (err) {
				console.error(err);
				setResults([]);
			} finally {
				// A search that matched nobody has to say so — silence is what made the
				// old control read as broken.
				setHasSearched(true);
				setIsOpen(true);
			}
		}, SEARCH_DEBOUNCE_MS);

		return () => clearTimeout(handle);
	}, [query, fetchSearch]);

	useEffect(() => {
		if (!isOpen) {
			return;
		}

		const updateRect = () => {
			const rect = anchorRef.current?.getBoundingClientRect();

			if (rect) {
				setMenuRect({ top: rect.bottom + 4, left: rect.left, width: rect.width });
			}
		};

		updateRect();
		window.addEventListener("scroll", updateRect, true);
		window.addEventListener("resize", updateRect);

		return () => {
			window.removeEventListener("scroll", updateRect, true);
			window.removeEventListener("resize", updateRect);
		};
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen) {
			return;
		}

		const handleOutsideClick = (event: MouseEvent) => {
			const target = getComposedEventTarget(event);

			if (
				target instanceof Node &&
				!anchorRef.current?.contains(target) &&
				!menuRef.current?.contains(target)
			) {
				setIsOpen(false);
			}
		};

		document.addEventListener("mousedown", handleOutsideClick);

		return () => document.removeEventListener("mousedown", handleOutsideClick);
	}, [isOpen]);

	const reset = () => {
		setIsOpen(false);
		setResults([]);
		setHasSearched(false);
		setQuery("");
	};

	const handleUseTypedUrl = () => {
		if (!typedUrl) {
			return;
		}

		onSelectUrl(typedUrl.url);
		reset();
	};

	const handlePick = (person: CompanyEnrichPreviewPerson) => {
		onSelectPerson(person);
		reset();
	};

	const typedUrlMenu = typedUrl ? (
		<button
			className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left hover:bg-gray-50"
			type="button"
			onClick={handleUseTypedUrl}
		>
			<Avatar
				firstName={nameParts(typedUrl.name).firstName}
				lastName={nameParts(typedUrl.name).lastName}
			/>
			<span className="min-w-0 flex-1">
				<span className="block truncate text-xs font-semibold">{typedUrl.name}</span>
				<span className="block truncate text-[11px] text-text-meta">{ts("addPersonUseUrl")}</span>
			</span>
		</button>
	) : null;

	const resultsMenu =
		isSearching && results.length === 0 ? (
			<div className="flex items-center justify-center p-3">
				<Spinner color="#4D66F5" />
			</div>
		) : results.length === 0 && hasSearched ? (
			<div className="px-3 py-2 text-[11px] text-text-meta">{ts("addPersonNoResults")}</div>
		) : (
			results.map((person) => (
				<button
					key={person.id}
					className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left hover:bg-gray-50"
					type="button"
					onClick={() => handlePick(person)}
				>
					<Avatar
						firstName={nameParts(person.name).firstName}
						imageSrc={person.image_url ?? undefined}
						lastName={nameParts(person.name).lastName}
					/>
					<span className="min-w-0 flex-1">
						<span className="block truncate text-xs font-semibold">{person.name}</span>
						{person.position && (
							<span className="block truncate text-[11px] text-text-meta">{person.position}</span>
						)}
					</span>
				</button>
			))
		);

	const menu =
		isOpen && menuRect ? (
			<div
				ref={menuRef}
				className="fixed z-50 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
				style={{ top: menuRect.top, left: menuRect.left, width: menuRect.width }}
			>
				{typedUrlMenu ?? resultsMenu}
			</div>
		) : null;

	return (
		<div ref={anchorRef} className="w-full">
			<input
				autoComplete="off"
				autoFocus
				className="input-styles h-9 w-full rounded-lg border px-3 text-xs"
				placeholder={ts("addPersonPlaceholder")}
				value={query}
				onChange={(event) => setQuery(event.target.value)}
				onFocus={() => {
					if (typedUrl || results.length > 0 || hasSearched) {
						setIsOpen(true);
					}
				}}
			/>
			{menu && ReactDOM.createPortal(menu, portalContainer ?? document.body)}
		</div>
	);
};
